// RouterPage — "Route berechnen" (3. NAVI-Kachel)
// ================================================
//
// ULTRA / Wave 14bv-navi: A→B-Routenplaner fürs Rennrad. Der User setzt einen
// Start (aktuelle GPS-Position ODER Tap auf die Karte) und ein Ziel (Tap auf
// die Karte) und bekommt DREI Varianten zwischen den Punkten:
//   1. Kürzeste            (absolut kürzeste Strecke)
//   2. Schnellste          (kürzeste Fahrzeit fürs Rennrad)
//   3. Wenigste Höhenmeter (flachste Route)
//
// Alle drei kommen aus EINEM Backend-Call (POST /api/navi/route). Das Routing
// nutzt nur asphaltierte Wege (Bundesstraßen erlaubt, Autobahn/Schotter raus)
// und läuft nur innerhalb des Korridors — das Backend meldet klare Fehler
// (out_of_corridor / too_far_apart), die wir freundlich anzeigen.
//
// Bewusst SCHLANK gehalten (kein Fork von CreateCustomPage): Default-MapLibre-
// Gesten (Pan/Zoom), Tap = map.on('click'), zwei Marker, eine Route-Linie.
// Kartendarstellung funktioniert offline (PMTiles); die Berechnung selbst
// braucht Online.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import maplibregl from 'maplibre-gl';
import { buildLiguriaStyle, buildMapoutStyle } from '../lib/map/style';
import { addPmtilesArchive, registerPmtilesProtocol } from '../lib/map/pmtiles';
import { opfsStatus } from '../lib/opfsStore';
import { tilesManifest } from '../lib/api';
import { CORRIDOR_CENTER, REGIONS } from '@adtu/shared';
import { positionBus } from '../lib/positionBus';
import { useOnline } from '../lib/useOnline';
import { useActiveRegion } from '../lib/useUiSettings';
import {
  dynamicRegionCenter,
  dynamicRegionName,
  isInsideAnyRegion,
} from '../lib/regionCheck';
import {
  postRouteAB,
  postResolveUrl,
  NaviRouteError,
  type RouteABResponse,
  type RouteVariant,
} from '../lib/naviApi';
import { saveCustomTrail, type CustomTrailCoord } from '../lib/customTrails';
import { parseLocationInput } from '../lib/parseLocationInput';

const ROUTE_SOURCE = 'adt-ab-route';
const ROUTE_CASING_LAYER = 'adt-ab-route-casing';
const ROUTE_LINE_LAYER = 'adt-ab-route-line';

type VariantKey = 'shortest' | 'fastest' | 'easiest';

const VARIANTS: Array<{ key: VariantKey; label: string; color: string }> = [
  { key: 'shortest', label: 'Kürzeste', color: '#3b82f6' },
  { key: 'fastest', label: 'Schnellste', color: '#f97316' },
  { key: 'easiest', label: 'Wenigste Höhenmeter', color: '#22c55e' },
];

// Welcher Pin wird als nächstes per Tap gesetzt.
type Picking = 'start' | 'end' | null;

// Letzte GPS-Position kurz persistieren, damit die Karte beim nächsten Öffnen
// SOFORT beim User startet (statt am Korridor-Zentrum = Mailand), auch bevor
// der erste frische Fix da ist.
const LAST_POS_KEY = 'adt_router_last_pos';
function readPersistedPos(): [number, number] | null {
  try {
    const raw = localStorage.getItem(LAST_POS_KEY);
    if (!raw) return null;
    const a = JSON.parse(raw);
    if (
      Array.isArray(a) &&
      a.length === 2 &&
      Number.isFinite(a[0]) &&
      Number.isFinite(a[1])
    ) {
      return [a[0], a[1]];
    }
  } catch {
    /* egal */
  }
  return null;
}
function persistPos(ll: [number, number]) {
  try {
    localStorage.setItem(LAST_POS_KEY, JSON.stringify(ll));
  } catch {
    /* egal */
  }
}

// Pulsierende „blauer-Punkt"-Keyframes einmalig injizieren.
function ensureGpsPulseStyle() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('adt-gps-pulse-style')) return;
  const s = document.createElement('style');
  s.id = 'adt-gps-pulse-style';
  s.textContent =
    '@keyframes adtGpsPulse{0%{transform:translate(-50%,-50%) scale(1);opacity:.45}70%{transform:translate(-50%,-50%) scale(2.6);opacity:0}100%{opacity:0}}';
  document.head.appendChild(s);
}

// Der blaue GPS-Punkt (aktuelle Position) — bewusst BLAU, klar unterscheidbar
// vom grünen Start- und roten Ziel-Pin. Kein Klick-Fänger (pointer-events off),
// damit Taps zum Setzen der Pins durchgehen.
function makeGpsDotEl(): HTMLDivElement {
  ensureGpsPulseStyle();
  const wrap = document.createElement('div');
  wrap.style.cssText =
    'width:22px;height:22px;position:relative;pointer-events:none';
  const pulse = document.createElement('div');
  pulse.style.cssText =
    'position:absolute;top:50%;left:50%;width:22px;height:22px;border-radius:50%;background:#3b82f6;animation:adtGpsPulse 2.2s ease-out infinite';
  const dot = document.createElement('div');
  dot.style.cssText =
    'position:absolute;top:50%;left:50%;width:15px;height:15px;transform:translate(-50%,-50%);border-radius:50%;background:#2563eb;border:2.5px solid #fff;box-shadow:0 0 3px rgba(0,0,0,.5)';
  wrap.appendChild(pulse);
  wrap.appendChild(dot);
  return wrap;
}

function fmtKm(m: number): string {
  return (m / 1000).toLocaleString('de-DE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}
function fmtDuration(s: number): string {
  const min = Math.round(s / 60);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const rem = min % 60;
  return `${h}:${String(rem).padStart(2, '0')} h`;
}

/** Backend-reason → freundliche deutsche Meldung. */
function messageForReason(
  reason: string | undefined,
  info?: { areas?: number },
): string {
  switch (reason) {
    case 'navi_expired':
      // Die 14-Tage-Frist der Navigationsdaten ist abgelaufen (oder der
      // User hat sie freigegeben, um einen anderen Korridor zu navigieren).
      return 'Für diesen Korridor gibt es keine Navigationsdaten mehr — sie werden 14 Tage nach dem Build gelöscht. Karte und POIs bleiben, fürs Routen den Korridor neu bauen.';
    case 'no_navi_data':
      return 'Für diesen Korridor wurden keine Navigationsdaten gebaut. Pro Person kann immer nur EIN Korridor navigieren — unter Corridor Downloads freigeben und neu bauen.';
    case 'both_outside':
    case 'start_outside':
    case 'end_outside':
    case 'out_of_corridor':
      return 'Punkt liegt außerhalb der Karte. Start und Ziel müssen im abgedeckten Korridor liegen.';
    case 'cross_segment':
    case 'too_far_apart':
      // Lange Korridore sind serverseitig in Bereiche zerlegt (ein Graph je
      // ~450 km). Geroutet wird immer innerhalb EINES Bereichs.
      return info?.areas && info.areas > 1
        ? `Start und Ziel liegen in verschiedenen Bereichen. Dieser Korridor ist in ${info.areas} Bereiche unterteilt (auf der Karte sichtbar) — es kann nur innerhalb eines Bereichs geroutet werden.`
        : 'Start und Ziel liegen zu weit auseinander (verschiedene Kartenbereiche). Wähle Punkte näher beieinander.';
    case 'start_no_paved_road':
      return 'Am Start ist keine asphaltierte Strasse in der Nähe. Setze den Punkt näher an eine Strasse.';
    case 'end_no_paved_road':
      return 'Am Ziel ist keine asphaltierte Strasse in der Nähe. Setze den Punkt näher an eine Strasse.';
    case 'start_equals_end':
      return 'Start und Ziel sind identisch.';
    case 'no_path_between_points':
    case 'no_route':
      return 'Zwischen den Punkten wurde keine asphaltierte Route gefunden.';
    default:
      return 'Route konnte nicht berechnet werden. Bitte erneut versuchen.';
  }
}

export function RouterPage() {
  const online = useOnline();
  const navigate = useNavigate();
  const [activeRegion] = useActiveRegion();
  // Klarname der aktiven Region fuers Kopf-Panel: statisch aus REGIONS,
  // sonst aus dem Tiles-Manifest (selbst gebaute Korridore).
  const activeRegionLabel =
    REGIONS[activeRegion]?.humanName ?? dynamicRegionName(activeRegion);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const startMarkerRef = useRef<maplibregl.Marker | null>(null);
  const endMarkerRef = useRef<maplibregl.Marker | null>(null);
  // Blauer GPS-Punkt (immer sichtbar, unabhängig von Start/Ziel-Pin).
  const gpsDotRef = useRef<maplibregl.Marker | null>(null);
  const gpsWatchRef = useRef<number | null>(null);
  const lastFixRef = useRef<[number, number] | null>(null);
  // Sobald der User selbst pannt/zoomt, keine Auto-Zentrierung mehr aufdrängen.
  const userInteractedRef = useRef(false);
  const centeredOnceRef = useRef(false);

  const [startMode, setStartMode] = useState<'gps' | 'map'>('gps');
  const [start, setStart] = useState<[number, number] | null>(null);
  const [end, setEnd] = useState<[number, number] | null>(null);
  const [picking, setPicking] = useState<Picking>(null);
  const pickingRef = useRef<Picking>(null);
  pickingRef.current = picking;

  const [result, setResult] = useState<RouteABResponse | null>(null);
  const [selected, setSelected] = useState<VariantKey>('shortest');
  const [loading, setLoading] = useState(false);
  const [slowHint, setSlowHint] = useState(false);
  const slowTimerRef = useRef<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [gpsBusy, setGpsBusy] = useState(false);

  // Save-in-Custom-Tours-Flow: 'idle' → 'naming' (Checkboxen + Namensfeld) →
  // 'saving' → 'saved' (Bestätigung + „In Custom Tours öffnen").
  const [savePhase, setSavePhase] = useState<'idle' | 'naming' | 'saving' | 'saved'>(
    'idle',
  );
  // Welche der drei Varianten gespeichert werden (der User kann beliebig viele
  // ankreuzen). Vorbelegt mit der aktuell gewählten.
  const [saveChecks, setSaveChecks] = useState<Record<VariantKey, boolean>>({
    shortest: false,
    fastest: false,
    easiest: false,
  });
  const [saveName, setSaveName] = useState('');
  const [savedCount, setSavedCount] = useState(0);

  // Aktuell gezeichnete Route-GeoJSON + Farbe — muss nach jedem setStyle
  // (PMTiles-Upgrade wischt Sources/Layer weg) neu angelegt werden.
  const routeGeoRef = useRef<GeoJSON.Feature | null>(null);
  const routeColorRef = useRef<string>('#3b82f6');

  // --- Marker setzen/aktualisieren ---------------------------------------
  function placeMarker(kind: 'start' | 'end', lonlat: [number, number]) {
    const map = mapRef.current;
    if (!map) return;
    const ref = kind === 'start' ? startMarkerRef : endMarkerRef;
    const color = kind === 'start' ? '#22c55e' : '#ef4444';
    if (!ref.current) {
      ref.current = new maplibregl.Marker({ color }).setLngLat(lonlat).addTo(map);
    } else {
      ref.current.setLngLat(lonlat);
    }
  }

  // --- Route-Layer (idempotent, überlebt setStyle nicht → re-add) ---------
  function ensureRouteLayer(map: maplibregl.Map) {
    if (!map.getSource(ROUTE_SOURCE)) {
      map.addSource(ROUTE_SOURCE, {
        type: 'geojson',
        data: routeGeoRef.current ?? { type: 'FeatureCollection', features: [] },
      });
    }
    if (!map.getLayer(ROUTE_CASING_LAYER)) {
      map.addLayer({
        id: ROUTE_CASING_LAYER,
        type: 'line',
        source: ROUTE_SOURCE,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': '#0b0f14',
          'line-width': ['interpolate', ['linear'], ['zoom'], 8, 6, 16, 12],
          'line-opacity': 0.5,
        },
      });
    }
    if (!map.getLayer(ROUTE_LINE_LAYER)) {
      map.addLayer({
        id: ROUTE_LINE_LAYER,
        type: 'line',
        source: ROUTE_SOURCE,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': routeColorRef.current,
          'line-width': ['interpolate', ['linear'], ['zoom'], 8, 3.5, 16, 7],
        },
      });
    } else {
      map.setPaintProperty(ROUTE_LINE_LAYER, 'line-color', routeColorRef.current);
    }
  }

  function drawVariant(variant: RouteVariant | null, color: string) {
    const map = mapRef.current;
    routeColorRef.current = color;
    if (!variant) {
      routeGeoRef.current = null;
      if (map?.getSource(ROUTE_SOURCE)) {
        (map.getSource(ROUTE_SOURCE) as maplibregl.GeoJSONSource).setData({
          type: 'FeatureCollection',
          features: [],
        });
      }
      return;
    }
    const feature: GeoJSON.Feature = {
      type: 'Feature',
      properties: {},
      geometry: variant.geometry,
    };
    routeGeoRef.current = feature;
    if (!map) return;
    ensureRouteLayer(map);
    (map.getSource(ROUTE_SOURCE) as maplibregl.GeoJSONSource | undefined)?.setData(
      feature,
    );
    map.setPaintProperty(ROUTE_LINE_LAYER, 'line-color', color);
  }

  function fitToVariant(variant: RouteVariant) {
    const map = mapRef.current;
    if (!map) return;
    const coords = variant.geometry.coordinates;
    if (coords.length === 0) return;
    let minLon = Infinity;
    let minLat = Infinity;
    let maxLon = -Infinity;
    let maxLat = -Infinity;
    for (const [lo, la] of coords) {
      if (lo < minLon) minLon = lo;
      if (lo > maxLon) maxLon = lo;
      if (la < minLat) minLat = la;
      if (la > maxLat) maxLat = la;
    }
    map.fitBounds(
      [
        [minLon, minLat],
        [maxLon, maxLat],
      ],
      { padding: { top: 130, bottom: 190, left: 40, right: 40 }, duration: 500 },
    );
  }

  // --- MapLibre-Init (Style-Pattern wie CreateCustomPage) ----------------
  useEffect(() => {
    if (!containerRef.current) return;
    registerPmtilesProtocol();

    // Start-Kamera: aktuelle/letzte GPS-Position statt Korridor-Zentrum
    // (Mailand). Reihenfolge: laufende Position (positionBus, von der Haupt-
    // Karte gefüttert) → persistierte letzte Position → Korridor-Zentrum.
    const busPos = positionBus.get();
    const knownPos: [number, number] | null = busPos
      ? [busPos.longitude, busPos.latitude]
      : readPersistedPos();
    // Wave 14ec: bei einem selbst gebauten Korridor kennt REGIONS den Slug
    // nicht — dann Bbox-Mitte des Korridors aus dem Manifest nehmen, sonst
    // oeffnet die Karte ueber Mailand waehrend der User in Andorra steht.
    const initialCenter: [number, number] =
      knownPos ??
      REGIONS[activeRegion]?.center ??
      dynamicRegionCenter(activeRegion) ??
      CORRIDOR_CENTER;
    // Nur bei LIVE-Position (positionBus, aktuelle Session) das Auto-Fly
    // unterdrücken — die persistierte Position kann von gestern sein, da wollen
    // wir beim ersten frischen Fix trotzdem hinfliegen.
    if (busPos) centeredOnceRef.current = true;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: buildMapoutStyle(),
      center: initialCenter,
      zoom: knownPos ? 13 : 11,
      attributionControl: { compact: true },
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false,
    });
    mapRef.current = map;

    // User-Interaktion merken → danach keine Auto-Zentrierung mehr.
    const markInteracted = () => {
      userInteractedRef.current = true;
    };
    map.on('dragstart', markInteracted);
    map.on('zoomstart', markInteracted);
    map.on('rotatestart', markInteracted);

    // Blauer GPS-Punkt: eigener watchPosition (Permission ist i.d.R. schon
    // erteilt, weil die Haupt-Karte GPS nutzt). Punkt immer sichtbar + wandert
    // mit; einmalig auf die Position zentrieren (nur wenn der User noch nicht
    // selbst gepannt hat). Kein Auto-Follow → kein Kampf gegens Pannen.
    if ('geolocation' in navigator) {
      gpsWatchRef.current = navigator.geolocation.watchPosition(
        (pos) => {
          const ll: [number, number] = [pos.coords.longitude, pos.coords.latitude];
          lastFixRef.current = ll;
          positionBus.set(pos.coords);
          persistPos(ll);
          if (!gpsDotRef.current) {
            gpsDotRef.current = new maplibregl.Marker({
              element: makeGpsDotEl(),
            })
              .setLngLat(ll)
              .addTo(map);
          } else {
            gpsDotRef.current.setLngLat(ll);
          }
          if (!centeredOnceRef.current && !userInteractedRef.current) {
            centeredOnceRef.current = true;
            map.easeTo({ center: ll, zoom: 13, duration: 600 });
          }
        },
        () => {
          /* keine Permission / kein Fix → kein Punkt, kein Fehler */
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 2000 },
      );
    }

    // Richtigen (PMTiles-)Style async nachladen — best effort.
    let aborted = false;
    (async () => {
      try {
        const manifest = await tilesManifest();
        if (aborted) return;
        const regionEntry =
          manifest.regions?.find((r) => r.id === activeRegion) ??
          manifest.regions?.[0] ??
          null;
        const mapEntry = regionEntry?.map ?? manifest.map;
        const contoursEntry = regionEntry?.contours ?? manifest.contours;
        const hillshadeEntry = regionEntry?.hillshade ?? manifest.hillshade;
        if (!mapEntry) return;

        const mapUrl = `pmtiles://${location.origin}${mapEntry.url}`;
        const contourUrl = contoursEntry
          ? `pmtiles://${location.origin}${contoursEntry.url}`
          : undefined;
        const hillshadeAvailable = hillshadeEntry
          ? (await opfsStatus(`${location.origin}${hillshadeEntry.url}`)).exists
          : false;
        if (aborted) return;
        const hillshadeUrl =
          hillshadeAvailable && hillshadeEntry
            ? `pmtiles://${location.origin}${hillshadeEntry.url}`
            : undefined;

        addPmtilesArchive(mapUrl.slice('pmtiles://'.length));
        if (contourUrl) addPmtilesArchive(contourUrl.slice('pmtiles://'.length));
        if (hillshadeUrl) addPmtilesArchive(hillshadeUrl.slice('pmtiles://'.length));

        map.setStyle(
          buildLiguriaStyle({
            mapPmtilesUrl: mapUrl,
            contourPmtilesUrl: contourUrl,
            hillshade: !!hillshadeUrl,
            hillshadePmtilesUrl: hillshadeUrl,
          }),
          { diff: false },
        );
      } catch (err) {
        console.warn('[router] style upgrade failed, sticking to mapout', err);
      }
    })();

    // Nach jedem style.load (auch nach setStyle) Route-Layer neu anlegen.
    map.on('style.load', () => {
      ensureRouteLayer(map);
    });

    // Tap → Pin setzen (nur wenn gerade ein Pin "scharf" ist).
    map.on('click', (e) => {
      const mode = pickingRef.current;
      if (!mode) return;
      const lonlat: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      if (!isInsideAnyRegion(lonlat[1], lonlat[0])) {
        setError(
          'Dieser Punkt liegt außerhalb der Karte. Bitte innerhalb des Korridors tippen.',
        );
        return;
      }
      setError(null);
      if (mode === 'start') {
        setStart(lonlat);
        placeMarker('start', lonlat);
      } else {
        setEnd(lonlat);
        placeMarker('end', lonlat);
      }
      // Ergebnis verwerfen — Punkte haben sich geändert.
      setResult(null);
      setSavePhase('idle');
      setSavedCount(0);
      drawVariant(null, routeColorRef.current);
      setPicking(null);
    });

    return () => {
      aborted = true;
      if (gpsWatchRef.current != null) {
        navigator.geolocation.clearWatch(gpsWatchRef.current);
        gpsWatchRef.current = null;
      }
      map.remove();
      mapRef.current = null;
      startMarkerRef.current = null;
      endMarkerRef.current = null;
      gpsDotRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRegion]);

  // --- Aktionen -----------------------------------------------------------
  async function handleUseGps() {
    if (!('geolocation' in navigator)) {
      setError('Standort wird von diesem Gerät nicht unterstützt.');
      return;
    }
    setGpsBusy(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGpsBusy(false);
        const lonlat: [number, number] = [
          pos.coords.longitude,
          pos.coords.latitude,
        ];
        if (!isInsideAnyRegion(lonlat[1], lonlat[0])) {
          setError(
            'Deine aktuelle Position liegt außerhalb der Karte. Nutze stattdessen „Auf Karte".',
          );
          return;
        }
        setStart(lonlat);
        setStartMode('gps');
        placeMarker('start', lonlat);
        setResult(null);
        resetSave();
        drawVariant(null, routeColorRef.current);
        mapRef.current?.easeTo({ center: lonlat, zoom: 13, duration: 500 });
      },
      (err) => {
        setGpsBusy(false);
        setError(
          err.code === err.PERMISSION_DENIED
            ? 'Standort-Zugriff verweigert. Erlaube ihn in den Einstellungen oder nutze „Auf Karte".'
            : 'Standort konnte nicht ermittelt werden.',
        );
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
    );
  }

  function pickStartOnMap() {
    setStartMode('map');
    setPicking('start');
    setError(null);
  }
  function pickEndOnMap() {
    setPicking('end');
    setError(null);
  }

  async function handleCompute() {
    if (!start || !end) return;
    if (!online) {
      setError('Für die Routenberechnung online gehen.');
      return;
    }
    setLoading(true);
    setError(null);
    setSlowHint(false);
    resetSave();
    if (slowTimerRef.current) window.clearTimeout(slowTimerRef.current);
    // Beim ersten Mal in einem neuen Kartenbereich kann der Server den Graph
    // bauen (~15s+). Nach 15s einen beruhigenden Hinweis zeigen.
    slowTimerRef.current = window.setTimeout(() => setSlowHint(true), 15000);
    try {
      const res = await postRouteAB(start, end, activeRegion);
      setResult(res);
      // Erste verfügbare Variante auswählen (normal 'shortest').
      const first: VariantKey =
        (res.shortest && 'shortest') ||
        (res.fastest && 'fastest') ||
        (res.easiest && 'easiest') ||
        'shortest';
      setSelected(first);
      const meta = VARIANTS.find((v) => v.key === first)!;
      const variant = res[first];
      drawVariant(variant, meta.color);
      if (variant) fitToVariant(variant);
    } catch (err) {
      if (err instanceof NaviRouteError) {
        setError(messageForReason(err.reason, err.info));
      } else {
        setError('Netzwerkfehler. Bitte erneut versuchen.');
      }
      setResult(null);
      drawVariant(null, routeColorRef.current);
    } finally {
      setLoading(false);
      setSlowHint(false);
      if (slowTimerRef.current) {
        window.clearTimeout(slowTimerRef.current);
        slowTimerRef.current = null;
      }
    }
  }

  function resetSave() {
    setSavePhase('idle');
    setSavedCount(0);
  }

  function selectVariant(key: VariantKey) {
    if (!result) return;
    setSelected(key);
    resetSave(); // andere Variante gewählt → Save-Flow zurücksetzen
    const meta = VARIANTS.find((v) => v.key === key)!;
    const variant = result[key];
    drawVariant(variant, meta.color);
    if (variant) fitToVariant(variant);
  }

  // --- Save in Custom Tours ----------------------------------------------
  // Reihenfolge für Checkboxen + Speicher-Reihenfolge.
  const CHECK_ORDER: VariantKey[] = ['shortest', 'fastest', 'easiest'];

  /** Aktuell angekreuzte Varianten (nur die, die es auch gibt), in fester
   *  Reihenfolge kürzeste → schnellste → wenigste Hm. */
  function checkedKeys(): VariantKey[] {
    return CHECK_ORDER.filter((k) => saveChecks[k] && !!result && !!result[k]);
  }

  /** Name für eine Variante beim Speichern:
   *  - Genau EINE gewählt: das Namensfeld IST der volle Name (Default
   *    „{Profil} · {km} km"), frei editierbar.
   *  - MEHRERE gewählt: optionaler Basis-Name + „ · {Profil}"; ohne Basis
   *    fällt jede auf „{Profil} · {km} km" zurück. So bekommt jede Route
   *    einen eindeutigen, unterscheidbaren Namen. */
  function nameForVariant(key: VariantKey): string {
    const meta = VARIANTS.find((v) => v.key === key)!;
    const v = result ? result[key] : null;
    const fallback = `${meta.label} · ${v ? fmtKm(v.distanceM) : ''} km`;
    const base = saveName.trim();
    if (checkedKeys().length <= 1) return base || fallback;
    return base ? `${base} · ${meta.label}` : fallback;
  }

  function startSave() {
    const checks: Record<VariantKey, boolean> = {
      shortest: false,
      fastest: false,
      easiest: false,
    };
    if (result && result[selected]) checks[selected] = true;
    setSaveChecks(checks);
    setSaveName('');
    setSavePhase('naming');
  }

  function toggleCheck(key: VariantKey) {
    setSaveChecks((c) => ({ ...c, [key]: !c[key] }));
  }

  async function confirmSave() {
    if (!result) return;
    const keys = checkedKeys();
    if (keys.length === 0) return;
    setSavePhase('saving');
    try {
      // Rückwärts speichern (wenigste Hm → schnellste → kürzeste), damit die
      // kürzeste den jüngsten createdAt bekommt und in der Custom-Tours-Liste
      // (neueste zuerst) oben in der Reihenfolge kürzeste → schnellste →
      // wenigste Hm erscheint. Sequenziell awaiten → createdAt unterscheidet
      // sich um ein paar ms.
      for (const key of [...keys].reverse()) {
        const v = result[key];
        if (!v || v.geometry.coordinates.length < 2) continue;
        const coords: CustomTrailCoord[] = v.geometry.coordinates.map(
          (c) => [c[0], c[1]] as CustomTrailCoord,
        );
        await saveCustomTrail({
          name: nameForVariant(key),
          coords,
          lengthM: v.distanceM,
          startPin: start ?? undefined,
          endPin: end ?? undefined,
          difficulty: 'unknown',
          source: 'router',
        });
      }
      setSavedCount(keys.length);
      setSavePhase('saved');
    } catch (err) {
      console.error('[router] save custom tour failed', err);
      setError('Speichern fehlgeschlagen. Bitte erneut versuchen.');
      setSavePhase('idle');
    }
  }

  // Karte auf die vorhandenen Pins (Start/Ziel) einpassen.
  function fitTwo(a: [number, number] | null, b: [number, number] | null) {
    const map = mapRef.current;
    if (!map) return;
    const pts = [a, b].filter(Boolean) as Array<[number, number]>;
    if (pts.length === 0) return;
    if (pts.length === 1) {
      map.easeTo({ center: pts[0], zoom: 13, duration: 500 });
      return;
    }
    let minLon = Infinity;
    let minLat = Infinity;
    let maxLon = -Infinity;
    let maxLat = -Infinity;
    for (const [lo, la] of pts) {
      minLon = Math.min(minLon, lo);
      maxLon = Math.max(maxLon, lo);
      minLat = Math.min(minLat, la);
      maxLat = Math.max(maxLat, la);
    }
    map.fitBounds(
      [
        [minLon, minLat],
        [maxLon, maxLat],
      ],
      { padding: { top: 160, bottom: 200, left: 50, right: 50 }, duration: 500, maxZoom: 14 },
    );
  }

  /** Start aus Koordinaten/Link setzen. Gibt false zurück wenn außerhalb Korridor. */
  function applyStart(lat: number, lon: number): boolean {
    if (!isInsideAnyRegion(lat, lon)) {
      setError('Dieser Punkt liegt außerhalb der Karte. Bitte innerhalb des Korridors.');
      return false;
    }
    setError(null);
    const ll: [number, number] = [lon, lat];
    setStart(ll);
    setStartMode('map');
    placeMarker('start', ll);
    setResult(null);
    resetSave();
    drawVariant(null, routeColorRef.current);
    fitTwo(ll, end);
    return true;
  }

  /** Ziel aus Koordinaten/Link setzen. Gibt false zurück wenn außerhalb Korridor. */
  function applyDestination(lat: number, lon: number): boolean {
    if (!isInsideAnyRegion(lat, lon)) {
      setError('Dieser Punkt liegt außerhalb der Karte. Bitte innerhalb des Korridors.');
      return false;
    }
    setError(null);
    const ll: [number, number] = [lon, lat];
    setEnd(ll);
    placeMarker('end', ll);
    setResult(null);
    resetSave();
    drawVariant(null, routeColorRef.current);
    fitTwo(start, ll);
    return true;
  }

  // Karte zurück auf den blauen GPS-Punkt zentrieren (nach freiem Pannen).
  function recenterToGps() {
    const ll = lastFixRef.current;
    if (ll && mapRef.current) {
      mapRef.current.easeTo({ center: ll, zoom: 14, duration: 500 });
    }
  }

  const canCompute = !!start && !!end && !loading;
  const selectedVariant = result ? result[selected] : null;

  // ------------------------------------------------------------------------
  return (
    <div className="absolute inset-0 bg-turbo-900">
      {/* Karte füllt alles */}
      <div ref={containerRef} className="absolute inset-0" />

      {/* „Auf meine Position zentrieren"-Button (blauer GPS-Punkt). Mittig am
          rechten Rand — kollidiert nie mit Kopf-/Fuß-Panel. */}
      <button
        onClick={recenterToGps}
        aria-label="Auf meine Position zentrieren"
        className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-turbo-900/90 backdrop-blur border border-turbo-700/60 text-sky-300 shadow-xl flex items-center justify-center active:scale-95 transition"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="3.2" />
          <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
        </svg>
      </button>

      {/* Kopf-Panel: Start/Ziel-Auswahl */}
      <div className="absolute inset-x-0 top-0 z-20 safe-top px-3 pt-3 pointer-events-none">
        <div className="pointer-events-auto rounded-2xl bg-turbo-900/92 backdrop-blur border border-turbo-700/50 p-3 shadow-xl">
          <div className="flex items-center gap-2 mb-2.5">
            <Link
              to="/navi"
              className="text-white/60 hover:text-white -ml-1 p-1 no-underline"
              aria-label="Zurück"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </Link>
            <h1 className="text-base font-bold text-sky-400 tracking-tight">
              Route berechnen
            </h1>
            {/* Wave 14ec: zeigt, in WELCHER Region geroutet wird. Bei selbst
                gebauten Korridoren war das vorher unsichtbar — der User
                tippte Punkte und bekam kryptische Korridor-Fehler, ohne zu
                wissen, welcher Korridor gerade aktiv ist. Umschalten:
                Profil - Region. */}
            {activeRegionLabel && (
              <span className="ml-auto max-w-[48%] truncate rounded-full border border-sky-400/30 bg-sky-500/10 px-2 py-0.5 text-[11px] font-semibold text-sky-300">
                {activeRegionLabel}
              </span>
            )}
          </div>

          {/* Start */}
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-block w-3 h-3 rounded-full bg-green-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-wide text-white/45 leading-none">
                Start
              </div>
              <div className="text-sm text-white truncate">
                {start
                  ? startMode === 'gps'
                    ? 'Aktuelle Position'
                    : `${start[1].toFixed(4)}, ${start[0].toFixed(4)}`
                  : 'noch nicht gesetzt'}
              </div>
            </div>
            <button
              onClick={handleUseGps}
              disabled={gpsBusy}
              className={`shrink-0 px-2.5 py-1.5 rounded-lg text-xs font-semibold border ${
                startMode === 'gps' && start
                  ? 'bg-green-500/20 border-green-500/60 text-green-300'
                  : 'bg-turbo-700/40 border-turbo-700/60 text-white/80'
              } active:scale-95 transition`}
            >
              {gpsBusy ? '…' : '📍 GPS'}
            </button>
            <button
              onClick={pickStartOnMap}
              className={`shrink-0 px-2.5 py-1.5 rounded-lg text-xs font-semibold border ${
                picking === 'start'
                  ? 'bg-sky-500/30 border-sky-400 text-white'
                  : 'bg-turbo-700/40 border-turbo-700/60 text-white/80'
              } active:scale-95 transition`}
            >
              🗺️ Karte
            </button>
          </div>
          <div className="mt-1.5 mb-2.5">
            <LocationInput accent="#22c55e" onApply={applyStart} />
          </div>

          {/* Ziel */}
          <div className="flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-full bg-red-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-wide text-white/45 leading-none">
                Ziel
              </div>
              <div className="text-sm text-white truncate">
                {end
                  ? `${end[1].toFixed(4)}, ${end[0].toFixed(4)}`
                  : 'noch nicht gesetzt'}
              </div>
            </div>
            <button
              onClick={pickEndOnMap}
              className={`shrink-0 px-2.5 py-1.5 rounded-lg text-xs font-semibold border ${
                picking === 'end'
                  ? 'bg-sky-500/30 border-sky-400 text-white'
                  : 'bg-turbo-700/40 border-turbo-700/60 text-white/80'
              } active:scale-95 transition`}
            >
              🗺️ Ziel wählen
            </button>
          </div>
          <div className="mt-1.5">
            <LocationInput accent="#ef4444" onApply={applyDestination} />
          </div>

          {picking && (
            <div className="mt-2 text-xs text-sky-300 bg-sky-500/10 rounded-lg px-2.5 py-1.5">
              Tippe auf die Karte, um {picking === 'start' ? 'den Start' : 'das Ziel'} zu
              setzen.
            </div>
          )}
          {error && (
            <div className="mt-2 text-xs text-amber-200 bg-amber-500/15 rounded-lg px-2.5 py-1.5">
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Fuß-Panel: Berechnen + Ergebnis-Varianten */}
      <div className="absolute inset-x-0 bottom-0 z-20 px-3 pb-3 pointer-events-none">
        <div className="pointer-events-auto rounded-2xl bg-turbo-900/92 backdrop-blur border border-turbo-700/50 p-3 shadow-xl">
          {!result && (
            <button
              onClick={handleCompute}
              disabled={!canCompute}
              className={`w-full py-3 rounded-xl font-bold text-sm transition ${
                canCompute
                  ? 'bg-sky-500 text-white active:bg-sky-600'
                  : 'bg-turbo-700/40 text-white/40'
              }`}
            >
              {loading
                ? 'Berechne …'
                : !online
                  ? 'Offline — zum Berechnen online gehen'
                  : !start || !end
                    ? 'Start und Ziel setzen'
                    : 'Berechnen'}
            </button>
          )}

          {loading && slowHint && (
            <div className="mt-2 text-xs text-sky-300 bg-sky-500/10 rounded-lg px-2.5 py-2 leading-snug">
              Berechne Route … das kann beim ersten Mal in diesem Kartenbereich
              bis zu 2 Minuten dauern. Bitte warten.
            </div>
          )}

          {result && (
            <div>
              {/* Varianten-Chips */}
              <div className="grid grid-cols-3 gap-2 mb-2.5">
                {VARIANTS.map((v) => {
                  const variant = result[v.key];
                  const active = selected === v.key;
                  return (
                    <button
                      key={v.key}
                      onClick={() => variant && selectVariant(v.key)}
                      disabled={!variant}
                      className={`rounded-xl px-2 py-2 text-center border transition ${
                        active
                          ? 'border-2'
                          : 'border border-turbo-700/60 opacity-90'
                      } ${!variant ? 'opacity-30' : 'active:scale-95'}`}
                      style={
                        active
                          ? { borderColor: v.color, background: `${v.color}22` }
                          : undefined
                      }
                    >
                      <div
                        className="text-[11px] font-bold leading-tight"
                        style={{ color: v.color }}
                      >
                        {v.label}
                      </div>
                      <div className="text-[11px] text-white/70 mt-0.5">
                        {variant ? `${fmtKm(variant.distanceM)} km` : '—'}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Stats der gewählten Variante */}
              {selectedVariant && (
                <div className="flex items-center justify-around bg-turbo-800/60 rounded-xl py-2 mb-2.5">
                  <Stat label="Strecke" value={`${fmtKm(selectedVariant.distanceM)} km`} />
                  <Stat label="Fahrzeit" value={fmtDuration(selectedVariant.durationS)} />
                  <Stat label="Anstieg" value={`${selectedVariant.ascentM} m ↑`} />
                </div>
              )}

              {/* Save-in-Custom-Tours */}
              {savePhase === 'idle' && (
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setResult(null);
                      resetSave();
                      drawVariant(null, routeColorRef.current);
                    }}
                    className="flex-1 py-2.5 rounded-lg text-xs font-semibold bg-turbo-700/40 text-white/80 active:scale-95 transition"
                  >
                    Neue Route
                  </button>
                  <button
                    onClick={startSave}
                    disabled={!selectedVariant}
                    className="flex-1 py-2.5 rounded-lg text-sm font-bold bg-sky-500/20 border border-sky-500/50 text-sky-200 active:scale-95 transition"
                  >
                    Route speichern
                  </button>
                </div>
              )}

              {(savePhase === 'naming' || savePhase === 'saving') && (
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-white/45 mb-1.5">
                    Welche Routen speichern?
                  </div>
                  <div className="flex flex-col gap-1.5 mb-2.5">
                    {VARIANTS.map((v) => {
                      const variant = result[v.key];
                      const disabled = !variant;
                      const checked = !!saveChecks[v.key] && !disabled;
                      return (
                        <button
                          key={v.key}
                          onClick={() => !disabled && toggleCheck(v.key)}
                          disabled={disabled}
                          className={`flex items-center gap-2.5 rounded-lg px-3 py-2 border text-left transition ${
                            disabled
                              ? 'opacity-30 border-turbo-700/40'
                              : checked
                                ? 'border-2 active:scale-[0.99]'
                                : 'border border-turbo-700/60 active:scale-[0.99]'
                          }`}
                          style={
                            checked ? { borderColor: v.color, background: `${v.color}18` } : undefined
                          }
                        >
                          <span
                            className="inline-flex items-center justify-center w-5 h-5 rounded shrink-0 border-2"
                            style={{
                              borderColor: v.color,
                              background: checked ? v.color : 'transparent',
                            }}
                          >
                            {checked && (
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#0b0f14" strokeWidth={3.5} strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 6L9 17l-5-5" />
                              </svg>
                            )}
                          </span>
                          <span className="text-sm font-bold" style={{ color: v.color }}>
                            {v.label}
                          </span>
                          <span className="ml-auto text-[11px] text-white/60">
                            {variant
                              ? `${fmtKm(variant.distanceM)} km · ↑${variant.ascentM} m`
                              : '—'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <input
                    type="text"
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg bg-turbo-800 border border-turbo-700/60 text-white text-sm mb-1.5 outline-none focus:border-sky-500"
                    placeholder={
                      checkedKeys().length > 1
                        ? 'Basis-Name (optional) — z. B. Alès → Narbonne'
                        : 'Name (optional)'
                    }
                  />
                  <div className="text-[11px] text-white/50 mb-2 leading-snug">
                    {checkedKeys().length === 0
                      ? 'Mindestens eine Route wählen.'
                      : `Speichert: ${checkedKeys().map(nameForVariant).join(' · ')}`}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSavePhase('idle')}
                      disabled={savePhase === 'saving'}
                      className="flex-1 py-2.5 rounded-lg text-xs font-semibold bg-turbo-700/40 text-white/80 active:scale-95 transition"
                    >
                      Abbrechen
                    </button>
                    <button
                      onClick={confirmSave}
                      disabled={savePhase === 'saving' || checkedKeys().length === 0}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition ${
                        savePhase === 'saving' || checkedKeys().length === 0
                          ? 'bg-turbo-700/40 text-white/40'
                          : 'bg-sky-500 text-white active:bg-sky-600'
                      }`}
                    >
                      {savePhase === 'saving'
                        ? 'Speichere …'
                        : checkedKeys().length > 1
                          ? `${checkedKeys().length} Routen speichern`
                          : 'Speichern'}
                    </button>
                  </div>
                </div>
              )}

              {savePhase === 'saved' && (
                <div>
                  <div className="flex items-center gap-2 text-sm font-semibold text-green-300 bg-green-500/10 rounded-lg px-3 py-2.5 mb-2">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    {savedCount === 1
                      ? '1 Route in Custom Tours gespeichert'
                      : `${savedCount} Routen in Custom Tours gespeichert`}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setResult(null);
                        resetSave();
                        drawVariant(null, routeColorRef.current);
                      }}
                      className="flex-1 py-2.5 rounded-lg text-xs font-semibold bg-turbo-700/40 text-white/80 active:scale-95 transition"
                    >
                      Neue Route
                    </button>
                    <button
                      onClick={() => navigate('/navi/custom-trails')}
                      className="flex-1 py-2.5 rounded-lg text-sm font-bold bg-sky-500 text-white active:bg-sky-600 transition"
                    >
                      In Custom Tours öffnen
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Kompaktes Eingabefeld für Koordinaten ODER Google-Maps-Link. Live-Parse:
 * grünes ✓ + Koordinaten wenn erkannt. „→"/Enter wendet an. Gekürzte Links
 * (maps.app.goo.gl) werden über das Backend aufgelöst.
 *
 * `onApply(lat, lon)` liefert true wenn übernommen (sonst z. B. außerhalb
 * Korridor → Feld bleibt stehen).
 */
function LocationInput({
  accent,
  onApply,
}: {
  accent: string;
  onApply: (lat: number, lon: number) => boolean;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const parsed = useMemo(() => parseLocationInput(text), [text]);
  const preview =
    parsed && (parsed.kind === 'coords' || parsed.kind === 'url') ? parsed : null;

  async function apply() {
    setErr(null);
    if (!text.trim()) return;
    if (!parsed) {
      setErr('Nicht erkannt — Koordinaten „lat, lon" oder Google-Maps-Link.');
      return;
    }
    if (parsed.kind === 'shortener') {
      setBusy(true);
      try {
        const r = await postResolveUrl(parsed.url);
        if (onApply(r.lat, r.lon)) setText('');
      } catch {
        setErr('Link konnte nicht aufgelöst werden. Online sein & erneut versuchen.');
      } finally {
        setBusy(false);
      }
      return;
    }
    if (onApply(parsed.lat, parsed.lon)) setText('');
  }

  return (
    <div>
      <div className="flex gap-1.5">
        <input
          type="text"
          inputMode="text"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setErr(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void apply();
            }
          }}
          placeholder="Koordinaten oder Google-Maps-Link"
          className="flex-1 min-w-0 px-2.5 py-1.5 rounded-lg bg-turbo-800 border border-turbo-700/60 text-white text-[13px] outline-none focus:border-sky-500"
        />
        <button
          onClick={() => void apply()}
          disabled={busy || !text.trim()}
          aria-label="Anwenden"
          className={`shrink-0 w-9 rounded-lg text-base font-bold flex items-center justify-center active:scale-95 transition ${
            busy || !text.trim() ? 'bg-turbo-700/40 text-white/40' : 'text-white'
          }`}
          style={busy || !text.trim() ? undefined : { background: accent }}
        >
          {busy ? '…' : '→'}
        </button>
      </div>
      {preview && (
        <div className="text-[11px] text-green-300 mt-1">
          ✓ {preview.lat.toFixed(5)}, {preview.lon.toFixed(5)}
          {parsed?.kind === 'url' ? ' (aus Link)' : ''}
        </div>
      )}
      {parsed?.kind === 'shortener' && !err && (
        <div className="text-[11px] text-white/50 mt-1">
          Kurzlink erkannt — „→" tippen zum Auflösen.
        </div>
      )}
      {err && <div className="text-[11px] text-amber-200 mt-1">{err}</div>}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-center px-1">
      <div className="text-[10px] uppercase tracking-wide text-white/45 leading-none mb-1">
        {label}
      </div>
      <div className="text-sm font-bold text-white">{value}</div>
    </div>
  );
}
